import { PostStatus } from "@/constants";
import { useDisclosure } from "@/hooks";
import { useUpdatePost } from "@/hooks/query-hooks/usePosts";
import { createFileFromUrl } from "@/libs/utils";
import { Button, Form, Input, Modal, Space, UploadFile } from "antd";
import { UploadChangeParam } from "antd/es/upload";
import compressImage, { Options } from "browser-image-compression";
import { useEffect, useState } from "react";
import { AddedImages, AddedImagesPreview, ImageUploadFormItem, PostImage } from "./components";

export function EditPostModal(props: Post) {

    const [opened, { toggle }] = useDisclosure(false);
    const { id, images, status } = props;
    const [form] = Form.useForm();

    const [previousImages, setPreviousImages] = useState<PostImage[]>([]);
    const [isEditingImages, setIsEditingImages] = useState(false);
    const [isCompressing, setIsCompressing] = useState(false);

    const { mutate, isUpdating } = useUpdatePost();

    useEffect(() => {
        if (images && images.length > 0) {
            onTransformImagesFromUrl(images).then((transformedImages) => {
                setPreviousImages(transformedImages)
                form.setFieldsValue(props);
                form.setFieldValue("uploadedImages", {
                    file: transformedImages[0]?.file || null,
                    fileList: transformedImages.map(img => img.file)
                })
            })
        } else {
            setPreviousImages([]);
        }

        if (opened) {
            setIsEditingImages(false);
        }
    }, [form, images, opened, props])

    const onFinish = async (values: {
        uploadedImages: { file: UploadFile, fileList: UploadFile[] };
        title: string;
        body: string;
    }) => {

        const { uploadedImages, title, body } = values;

        let imageIdsToKeep: number[] = [];
        let compressedImages: File[] = [];
        const compressOptions: Options = {
            maxSizeMB: 3,
            useWebWorker: true,
        }

        if (uploadedImages?.fileList.length > 0) {
            imageIdsToKeep = uploadedImages.fileList.filter(file => images.some(img => img.id.toString() === file.uid)).map(file => parseInt(file.uid, 10));

            const newlyUploadedImages = uploadedImages.fileList.filter(file => !imageIdsToKeep.includes(parseInt(file.uid, 10)));

            try {
                compressedImages = await Promise.all(newlyUploadedImages.map(async (file) => {
                    const compressedFile = await compressImage(file.originFileObj as File, compressOptions);
                    return new File([compressedFile], file.name, { type: file.type });
                }))
            } catch (error) {
                return;
            } finally {
                setIsCompressing(false);
            }
        }

        const data = new Blob([JSON.stringify({
            title,
            body,
            imageIdsToKeep
        })], { type: 'application/json' });
        const formData = new FormData();

        formData.append("data", data);
        compressedImages.forEach((image) => {
            formData.append(`images`, image);
        });

        mutate({
            id,
            status: status as PostStatus,
            formData
        }, {
            onSuccess: () => {
                toggle();
            }
        })

    }

    const onTransformImagesFromUrl = async (images: { id: number, url: string }[]) => {
        const formattedImages = await Promise.all(images.map(async (image) => {
            const file = await createFileFromUrl(image.url, image.url.split('/').pop() || 'image', 'image/jpeg');

            return {
                imgUrl: URL.createObjectURL(file),
                file: {
                    uid: image.id.toString(),
                    name: image.url.split('/').pop() || 'image',
                    originFileObj: file,
                } as UploadFile
            };
        }));

        return formattedImages;
    }

    const onImageChange = async (info: UploadChangeParam<UploadFile>) => {
        const { fileList } = info;
        const uploadedImages = fileList.map((file) => {
            return {
                imgUrl: URL.createObjectURL(file.originFileObj as File),
                file: file,
            }
        });
        setPreviousImages(uploadedImages);
    };

    const onRemoveImage = (image: PostImage) => {
        const filteredImages = previousImages.filter(img => img.imgUrl !== image.imgUrl);
        setPreviousImages(filteredImages);
        form.setFieldsValue({
            uploadedImages: {
                fileList: filteredImages.map(img => img.file)
            }
        });
        if (images.length === 1) {
            setIsEditingImages(false);
        }
    }

    return (
        <>
            <Button
                type="link"
                onClick={toggle}
                size="small"
            >
                Edit
            </Button>
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                name={`edit-post-form-${props.id}`}
                className="hidden"
            >
                <Modal
                    open={opened}
                    onCancel={toggle}
                    title={
                        isEditingImages ? "Images" : "Edit Post"
                    }
                    width={{
                        xs: "90%",
                        sm: "90%",
                        md: isEditingImages ? "100%" : "70%",
                        lg: isEditingImages ? "75%" : "50%",
                        xl: isEditingImages ? "65%" : "35%",
                    }}
                    okText="Save"
                    destroyOnClose
                    classNames={{
                        header: "[&>.ant-modal-title]:!text-xl [&>.ant-modal-title]:!font-poppins",
                        body: "max-h-[500px] min-h-[300px] overflow-y-auto !py-4",
                    }}
                    footer={(_, { OkBtn }) => (
                        <>
                            {
                                isEditingImages ? (
                                    <Space>
                                        <ImageUploadFormItem
                                            images={previousImages}
                                            onImageChange={onImageChange}
                                            variant="edit-add-more"
                                        />
                                        <Button
                                            className="!font-poppins"
                                            size="large"
                                            shape="round"
                                            onClick={() => setIsEditingImages(false)}
                                            type="primary"
                                        >
                                            Done
                                        </Button>
                                    </Space>
                                ) : (
                                    <div
                                        className="flex items-center justify-between gap-2"
                                    >
                                        {
                                            previousImages.length === 0 && (
                                                <ImageUploadFormItem
                                                    images={previousImages}
                                                    onImageChange={onImageChange}
                                                    variant="default"
                                                />
                                            )
                                        }
                                        <Space
                                            className="ml-auto"
                                        >
                                            <OkBtn />
                                        </Space>
                                    </div>
                                )
                            }
                        </>
                    )}
                    okButtonProps={{
                        type: "primary",
                        htmlType: "submit",
                        form: `edit-post-form-${props.id}`,
                        className: "bg-primary-gradient",
                        size: "large",
                        shape: "round",
                        loading: isUpdating || isCompressing,
                    }}
                    maskClosable={!isUpdating || !isCompressing}
                    centered
                >
                    {
                        isEditingImages ? (
                            <AddedImagesPreview
                                images={previousImages}
                                onRemoveImage={onRemoveImage}
                            />
                        ) : (
                            <>
                                <Form.Item
                                    name={"title"}
                                    rules={[{ required: true, message: "Please enter post title" }]}
                                    className="!mb-0"
                                >
                                    <Input.TextArea
                                        placeholder="Title . . ."
                                        autoSize={{ minRows: 1, maxRows: 3 }}
                                        className="!text-xl font-semibold !p-0"
                                        variant="borderless"
                                    />
                                </Form.Item>
                                <Form.Item
                                    name={"body"}
                                    rules={[{ required: true, message: "Please enter post content" }]}
                                    className="!mb-0"
                                >
                                    <Input.TextArea
                                        placeholder="Content. . ."
                                        autoSize={{ minRows: 3, maxRows: 5 }}
                                        className="!text-base !p-0"
                                        variant="borderless"
                                    />
                                </Form.Item>

                                {
                                    !isEditingImages && previousImages.length > 0 && (
                                        <AddedImages
                                            images={previousImages}
                                            onEdit={() => setIsEditingImages(true)}
                                            Component={(
                                                <ImageUploadFormItem
                                                    images={previousImages}
                                                    onImageChange={onImageChange}
                                                    variant="add-more"
                                                />
                                            )}
                                        />
                                    )
                                }
                            </>
                        )
                    }
                </Modal>
            </Form>
        </>
    )
}
